import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import AppLayout from '../../components/AppLayout';
import API_BASE_URL from '../../config/api';
import { toast } from 'react-toastify';

export default function ExpenseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setExpense(data);
      } else {
        toast.error('Failed to fetch expense details');
        navigate('/expenses');
      }
    } catch (error) {
      console.error('Error fetching expense:', error);
      toast.error('Error loading expense details');
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/expenses');
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const getOtherExpensesTotal = (otherExpenses) => {
    if (!otherExpenses || !Array.isArray(otherExpenses)) return 0;
    return otherExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#C4A574' }} />
        </Box>
      </AppLayout>
    );
  }

  if (!expense) {
    return (
      <AppLayout>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Expense not found
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={handleBack}
            sx={{ color: '#8B7355', bgcolor: 'transparent', '&:hover': { backgroundColor: 'rgba(139,115,85,0.06)' } }}
            aria-label="back"
            size="large"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 500, letterSpacing: 2, color: '#8B7355' }}>
            EXPENSE DETAILS
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Main Details */}
          <Grid item xs={12} xl={8} sx={{ width: { xs: '100%', xl: 'auto' } }}>
            <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 2, bgcolor: '#232323' }}>
              {/* Period Only */}
              <Box sx={{ mb: 4 }}>
                <Typography variant={{ xs: 'h5', md: 'h4' }} sx={{ fontWeight: 700, color: '#C4A574', mb: 1 }}>
                  {expense.FormattedDate}
                </Typography>
              </Box>

              <Divider sx={{ my: 3, bgcolor: 'rgba(196,165,116,0.08)' }} />

              {/* Fixed Expenses */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#C4A574', fontWeight: 600 }}>
                  Fixed Monthly Expenses
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: { xs: 2, md: 3 }, backgroundColor: 'rgba(196,165,116,0.12)', textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: '#C4A574', mb: 1 }}>
                        Rent
                      </Typography>
                      <Typography variant={{ xs: 'h6', md: 'h5' }} sx={{ fontWeight: 700, color: '#C59A58' }}>
                        {formatCurrency(expense.Rent)}
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: { xs: 2, md: 3 }, backgroundColor: 'rgba(196,165,116,0.12)', textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: '#C4A574', mb: 1 }}>
                        Electricity
                      </Typography>
                      <Typography variant={{ xs: 'h6', md: 'h5' }} sx={{ fontWeight: 700, color: '#C59A58' }}>
                        {formatCurrency(expense.Electricity)}
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} sm={12} md={4}>
                    <Paper sx={{ p: { xs: 2, md: 3 }, backgroundColor: 'rgba(196,165,116,0.12)', textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: '#C4A574', mb: 1 }}>
                        Snacks
                      </Typography>
                      <Typography variant={{ xs: 'h6', md: 'h5' }} sx={{ fontWeight: 700, color: '#C59A58' }}>
                        {formatCurrency(expense.Snacks)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Other Expenses */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#C4A574', fontWeight: 600 }}>
                  Other Expenses
                </Typography>
                
                {expense.OtherExpenses && expense.OtherExpenses.length > 0 ? (
                  <Box>
                    <Paper sx={{ p: { xs: 2, md: 3 }, backgroundColor: 'rgba(196,165,116,0.12)', mb: 3 }}>
                      <Typography variant={{ xs: 'h6', md: 'h5' }} sx={{ fontWeight: 700, color: '#C59A58', textAlign: 'center' }}>
                        {formatCurrency(getOtherExpensesTotal(expense.OtherExpenses))}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#C4A574', textAlign: 'center' }}>
                        Total Other Expenses
                      </Typography>
                    </Paper>
                    
                    <Grid container spacing={2}>
                      {expense.OtherExpenses.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper sx={{ p: { xs: 1.5, md: 2 }, backgroundColor: 'rgba(196, 165, 116, 0.08)' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C4A574', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                              {item.name}
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#C59A58', fontWeight: 700, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                              {formatCurrency(item.amount)}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : (
                  <Paper sx={{ p: { xs: 3, md: 4 }, backgroundColor: 'rgba(196, 165, 116, 0.08)', textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No other expenses recorded for this month
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Summary Sidebar */}
          <Grid item xs={12} xl={4} sx={{ width: { xs: '100%', xl: 'auto' } }}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 2, md: 3 }, 
                borderRadius: 2, 
                position: { md: 'sticky', xs: 'static' }, 
                top: { md: 20 }, 
                bgcolor: '#262626' 
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: '#C4A574', fontWeight: 600 }}>
                Expense Breakdown
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Fixed Expenses</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(parseFloat(expense.Rent) + parseFloat(expense.Electricity) + parseFloat(expense.Snacks))}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Other Expenses</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(getOtherExpensesTotal(expense.OtherExpenses))}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, bgcolor: 'rgba(196,165,116,0.08)' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#C4A574' }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#C59A58' }}>
                    {formatCurrency(expense.TotalAmount)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2, backgroundColor: 'rgba(196,165,116,0.12)', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Created: {new Date(expense.CreatedAt).toLocaleDateString()}
                </Typography>
                {expense.UpdatedAt !== expense.CreatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Updated: {new Date(expense.UpdatedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </AppLayout>
  );
}